import { Component, Prop, State, Method, Listen, h, Element, Build } from '@stencil/core';
import { getConstant } from '../../../../utils/constants-helpers';

const COMPONENT_NAME = 'cookie-type-1';

// Constants
const MAX_LINES = 2;
const LINE_HEIGHT_TOLERANCE = 2.1;
const HEIGHT_TOLERANCE = 2;
const DEFAULT_LINE_HEIGHT = 24;
const LINE_HEIGHT_MULTIPLIER = 1.5;
const RETRY_DELAY = 50;
const POSITIONING_DELAY = 10;
const CHECK_OVERFLOW_DELAY = 100;
const OPEN_PROGRAMMATICALLY_DELAY = 150;

// Cookie names
const COOKIE_REJECTED = 'COOKIES_REJECTED_UPDATED';
const COOKIE_AGREEMENT = 'COOKIES_AGREEMENT_UPDATED';

// Cookie helper functions
const setCookie = (name: string, value: string, options: { domain?: string; path?: string; expires?: number } = {}) => {
  let cookieString = `${name}=${value}`;

  if (options.domain) {
    cookieString += `; domain=${options.domain}`;
  }

  if (options.path) {
    cookieString += `; path=${options.path}`;
  } else {
    cookieString += `; path=/`;
  }

  // Set expiration to 1 year if not specified
  if (options.expires) {
    const date = new Date();
    date.setTime(date.getTime() + options.expires * 24 * 60 * 60 * 1000);
    cookieString += `; expires=${date.toUTCString()}`;
  } else {
    const date = new Date();
    date.setTime(date.getTime() + 365 * 24 * 60 * 60 * 1000); // 1 year
    cookieString += `; expires=${date.toUTCString()}`;
  }

  document.cookie = cookieString;
};

/**
 * Gets a cookie value by name
 */
const getCookie = (name: string): string | null => {
  const nameEQ = `${name}=`;
  const cookies = document.cookie.split(';');

  for (let i = 0; i < cookies.length; i++) {
    let cookie = cookies[i];
    while (cookie.charAt(0) === ' ') {
      cookie = cookie.substring(1, cookie.length);
    }
    if (cookie.indexOf(nameEQ) === 0) {
      return cookie.substring(nameEQ.length, cookie.length);
    }
  }

  return null;
};

/**
 * Checks if user has already interacted with the cookie banner
 */
const hasUserInteracted = (): boolean => {
  return getCookie(COOKIE_REJECTED) !== null || getCookie(COOKIE_AGREEMENT) !== null;
};

/**
 * Fixed-position cookie consent banner with accept/reject functionality and expandable description text.
 * Horizontal layout: description text with collapsible "Read more" toggle on the left, accept button and close icon on the right.
 * Auto-shows after configurable delay and manages cookie state for consent tracking.
 *
 * @category Popups
 * @constant COOKIE_BANNER_DESCRIPTION - "We use cookies to improve security, personalize the user experience, enhance our marketing activities (including cooperating with our 3rd party partners) and for other business use. Click <a href=\"/cookies\" title=\"Cookie Policy\">here</a> to&nbsp;read our Cookie Policy. By&nbsp;clicking \"Accept\" you agree to&nbsp;the use of&nbsp;cookies." - HTML content for cookie description (supports links)
 * @constant COOKIE_BANNER_ACCEPT - Accept - Text for Accept button
 * @constant COOKIE_BANNER_READ_MORE - "Read more" - Text for "Read more" link
 * @constant COOKIE_BANNER_READ_LESS - "Read less" - Text for "Read less" link
 * @example
 *   <cookie-type-1></cookie-type-1>
 */
@Component({
  tag: 'cookie-type-1',
  styleUrl: 'index.scss',
  shadow: false,
})
export class CookieType1 {
  @Element() el: HTMLElement;

  /**
   * Component constants object containing all required text constants
   */
  @Prop() constants?: Record<string, string>;

  /**
   * Delay before showing the cookie popup in milliseconds
   * @default 4000
   */
  @Prop() delay: number = 4000;

  /**
   * Label for close button (for accessibility)
   */
  @Prop() closeLabel: string = 'Close cookie popup';

  @State() isVisible: boolean = false;
  @State() isExpanded: boolean = false;
  @State() needsReadMore: boolean = false; // Will be determined by checkTextOverflow

  private timer: number;
  private slotContainer!: HTMLElement;
  private descriptionRef!: HTMLElement;
  private checkTextOverflowInProgress: boolean = false; // Prevent infinite loops

  /**
   * Gets description text from constants
   */
  private getDescription(): string {
    return getConstant(
      this.constants,
      'COOKIE_BANNER_DESCRIPTION',
      'We use cookies to improve security, personalize the user experience, enhance our marketing activities (including cooperating with our 3rd party partners) and for other business use. Click <a href="/cookies" title="Cookie Policy" aria-label="Cookie Policy">here</a> to&nbsp;read our Cookie Policy. By&nbsp;clicking "Accept" you agree to&nbsp;the use of&nbsp;cookies.',
    );
  }

  /**
   * Gets accept button text from constants
   */
  private getAcceptText(): string {
    return getConstant(this.constants, 'COOKIE_BANNER_ACCEPT', 'Accept');
  }

  /**
   * Gets "Read more" text from constants
   */
  private getReadMoreText(): string {
    return getConstant(this.constants, 'COOKIE_BANNER_READ_MORE', 'Read more');
  }

  /**
   * Gets "Read less" text from constants
   */
  private getReadLessText(): string {
    return getConstant(this.constants, 'COOKIE_BANNER_READ_LESS', 'Read less');
  }

  /**
   * Finds the inner content element (p, div, or span) within the description ref
   */
  private getInnerElement(): HTMLElement | null {
    if (!this.descriptionRef) return null;

    // Try to find the main content element (p or div first, not span which might be the read-more link)
    let innerElement = this.descriptionRef.querySelector('p, div') as HTMLElement;

    // If not found, try span as fallback
    if (!innerElement) {
      innerElement = this.descriptionRef.querySelector('span') as HTMLElement;
    }

    // In SSR+hydration mode me-text may render text directly without a wrapper element,
    // or the inner element may be empty before me-text finishes reconciling.
    // Fall back to descriptionRef (me-text itself) so overflow measurement still works.
    if (!innerElement || !innerElement.textContent?.trim()) {
      innerElement = this.descriptionRef;
    }

    return innerElement;
  }

  /**
   * Calculates the effective line height from computed styles
   */
  private calculateLineHeight(computedStyle: CSSStyleDeclaration, fontSize: number): number {
    const lineHeight = parseFloat(computedStyle.lineHeight);

    if (lineHeight && !isNaN(lineHeight) && lineHeight > 0) {
      return lineHeight;
    }

    if (fontSize && !isNaN(fontSize) && fontSize > 0) {
      return fontSize * LINE_HEIGHT_MULTIPLIER;
    }

    return DEFAULT_LINE_HEIGHT;
  }

  /**
   * Updates the description HTML and re-attaches event listeners
   */
  private updateDescriptionHTML(): void {
    if (!this.descriptionRef) return;

    const innerElement = this.getInnerElement();
    if (!innerElement) return;

    const htmlWithLink = this.getDescriptionWithReadMore();
    this.descriptionRef.setAttribute('innerHTML', htmlWithLink);
    innerElement.innerHTML = htmlWithLink;
  }

  /**
   * Resets absolute positioning for read-more link (used when expanding)
   */
  private resetReadMoreLinkPosition(): void {
    const innerElement = this.getInnerElement();
    if (!innerElement) return;

    const readMoreInline = innerElement.querySelector(`.${COMPONENT_NAME}__read-more-inline`) as HTMLElement;
    if (readMoreInline) {
      readMoreInline.style.position = 'relative';
      readMoreInline.style.left = 'auto';
      readMoreInline.style.top = 'auto';
      readMoreInline.style.right = 'auto';
      readMoreInline.style.bottom = 'auto';
    }
  }

  componentDidLoad() {
    if (!Build.isBrowser) return;

    // Check if user has already interacted with the banner
    if (hasUserInteracted()) {
      // User has already accepted or rejected cookies, don't show the banner
      return;
    }

    this.timer = window.setTimeout(() => {
      this.isVisible = true;
      // Check if text needs "Read more" after popup becomes visible and styles are applied
      // Use multiple requestAnimationFrame to ensure DOM is fully rendered and styles are applied
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setTimeout(() => {
            this.checkTextOverflow();
          }, CHECK_OVERFLOW_DELAY);
        });
      });
    }, this.delay);

    this.slotContainer?.addEventListener('click', this.handleSlotClick);
  }

  disconnectedCallback() {
    if (!Build.isBrowser) return;
    if (this.timer) {
      clearTimeout(this.timer);
    }
    this.slotContainer?.removeEventListener('click', this.handleSlotClick);
  }

  private checkTextOverflow = () => {
    if (!Build.isBrowser) return;
    // Prevent infinite loops
    if (this.checkTextOverflowInProgress || !this.descriptionRef || !this.isVisible) {
      return;
    }

    this.checkTextOverflowInProgress = true;

    const innerElement = this.getInnerElement();

    // If inner element not found, the me-text might not be fully rendered yet
    if (!innerElement) {
      this.checkTextOverflowInProgress = false;
      // Retry after a short delay if inner element is not ready
      setTimeout(() => {
        if (!this.checkTextOverflowInProgress) {
          this.checkTextOverflow();
        }
      }, RETRY_DELAY);
      return;
    }

    const wasExpanded = this.isExpanded;

    // Always remove collapsed class first to measure full height
    this.descriptionRef.classList.remove(`${COMPONENT_NAME}__description-text--collapsed`);

    // Temporarily set innerHTML without read more link to measure
    innerElement.innerHTML = this.getDescription();

    // Batch all geometry reads together to trigger a single reflow
    const computedStyle = getComputedStyle(innerElement);
    const fullHeight = innerElement.scrollHeight;
    const fontSize = parseFloat(computedStyle.fontSize);
    const effectiveLineHeight = this.calculateLineHeight(computedStyle, fontSize);
    const maxHeight = effectiveLineHeight * MAX_LINES;

    // Store maxHeight for CSS custom property
    this.descriptionRef.style.setProperty('--max-height-2-lines', `${maxHeight}px`);

    // Check if content exceeds MAX_LINES (with tolerance)
    const exceedsTwoLines = fullHeight > maxHeight + HEIGHT_TOLERANCE;

    if (exceedsTwoLines) {
      this.handleTextOverflow(wasExpanded, maxHeight);
    } else {
      this.handleTextFits();
    }
  };

  /**
   * Handles the case when text exceeds the maximum lines
   */
  private handleTextOverflow(wasExpanded: boolean, maxHeight: number): void {
    this.needsReadMore = true;

    if (!wasExpanded) {
      this.descriptionRef.classList.add(`${COMPONENT_NAME}__description-text--collapsed`);
      this.descriptionRef.style.setProperty('--max-height-2-lines', `${maxHeight}px`);
    }

    this.updateDescriptionHTML();

    setTimeout(() => {
      if (!wasExpanded) {
        this.positionReadMoreLink();
      }
      this.attachReadMoreListeners();
      this.checkTextOverflowInProgress = false;
    }, POSITIONING_DELAY);
  }

  /**
   * Handles the case when text fits within the maximum lines
   */
  private handleTextFits(): void {
    this.needsReadMore = false;
    this.descriptionRef.classList.remove(`${COMPONENT_NAME}__description-text--collapsed`);
    this.checkTextOverflowInProgress = false;
  }

  private handleReadMore = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    this.isExpanded = true;

    if (!this.descriptionRef) return;

    // Remove collapsed class
    this.descriptionRef.classList.remove(`${COMPONENT_NAME}__description-text--collapsed`);

    // Update HTML with read less link
    this.updateDescriptionHTML();

    // Reset positioning for read less link (it should be inline)
    this.resetReadMoreLinkPosition();

    // Re-attach event listeners
    setTimeout(() => {
      this.attachReadMoreListeners();
    }, POSITIONING_DELAY);
  };

  private handleReadLess = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    this.isExpanded = false;

    if (!this.descriptionRef) return;

    // Add collapsed class
    this.descriptionRef.classList.add(`${COMPONENT_NAME}__description-text--collapsed`);

    // Update HTML with read more link
    this.updateDescriptionHTML();

    // Re-position read more link
    setTimeout(() => {
      this.positionReadMoreLink();
      this.attachReadMoreListeners();
    }, POSITIONING_DELAY);

    // Scroll to top of description
    this.descriptionRef.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  };

  private positionReadMoreLink = () => {
    if (!this.descriptionRef || !this.needsReadMore || this.isExpanded) return;

    const innerElement = this.getInnerElement();
    if (!innerElement) return;

    const readMoreInline = innerElement.querySelector(`.${COMPONENT_NAME}__read-more-inline`) as HTMLElement;
    if (!readMoreInline) return;

    const computedStyle = getComputedStyle(innerElement);
    const fontSize = parseFloat(computedStyle.fontSize);
    const lineHeight = this.calculateLineHeight(computedStyle, fontSize);

    readMoreInline.style.display = 'none';

    if (!Build.isBrowser) return;
    const range = document.createRange();
    const walker = document.createTreeWalker(innerElement, NodeFilter.SHOW_TEXT, null);

    // Read container rect once — it doesn't change during measurement
    const containerRect = innerElement.getBoundingClientRect();
    const maxTop = lineHeight * LINE_HEIGHT_TOLERANCE;

    let found = false;
    let lastValidEnd = 0;
    let lastValidNode: Node | null = null;

    // Binary search per text node instead of linear character-by-character scan
    let textNode = walker.nextNode();
    while (textNode) {
      const nodeText = textNode.textContent || '';

      if (nodeText.length === 0) {
        textNode = walker.nextNode();
        continue;
      }

      range.setStart(textNode, 0);
      range.setEnd(textNode, nodeText.length);
      const endRect = range.getBoundingClientRect();

      if (endRect.top - containerRect.top <= maxTop) {
        lastValidEnd = nodeText.length;
        lastValidNode = textNode;
        textNode = walker.nextNode();
        continue;
      }

      range.setEnd(textNode, 1);
      const startRect = range.getBoundingClientRect();

      if (startRect.top - containerRect.top > maxTop) {
        found = lastValidNode !== null;
        break;
      }

      let lo = 1;
      let hi = nodeText.length;
      let bestIdx = 0;

      while (lo <= hi) {
        const mid = (lo + hi) >>> 1;
        range.setStart(textNode, 0);
        range.setEnd(textNode, mid);
        const midRect = range.getBoundingClientRect();

        if (midRect.top - containerRect.top <= maxTop) {
          bestIdx = mid;
          lo = mid + 1;
        } else {
          hi = mid - 1;
        }
      }

      lastValidEnd = bestIdx;
      lastValidNode = textNode;
      found = true;
      break;
    }

    if (!found && lastValidNode) {
      found = true;
    }

    readMoreInline.style.display = '';

    if (found && lastValidNode) {
      range.setStart(lastValidNode, 0);
      range.setEnd(lastValidNode, lastValidEnd);
      const rect = range.getBoundingClientRect();

      const leftPosition = rect.right - containerRect.left;
      const topPosition = lineHeight;

      readMoreInline.style.left = `${leftPosition}px`;
      readMoreInline.style.top = `${topPosition}px`;
    } else {
      readMoreInline.style.right = '0';
      readMoreInline.style.bottom = '0';
    }
  };

  private attachReadMoreListeners = () => {
    if (!this.descriptionRef) return;

    // Get the inner element rendered by me-text (p, span, or div)
    const innerElement = this.descriptionRef.querySelector('p, span, div') || this.descriptionRef;

    const readMoreLink = innerElement.querySelector(`.${COMPONENT_NAME}__read-more-link`);
    if (readMoreLink) {
      // Remove existing listeners by cloning
      const newLink = readMoreLink.cloneNode(true);
      readMoreLink.parentNode?.replaceChild(newLink, readMoreLink);

      newLink.addEventListener('click', e => {
        e.preventDefault();
        e.stopPropagation();
        if (this.isExpanded) {
          this.handleReadLess(e as MouseEvent);
        } else {
          this.handleReadMore(e as MouseEvent);
        }
      });
      newLink.addEventListener('keydown', e => {
        const keyEvent = e as KeyboardEvent;
        if (keyEvent.key === 'Enter' || keyEvent.key === ' ') {
          keyEvent.preventDefault();
          if (this.isExpanded) {
            this.handleReadLess(e as any);
          } else {
            this.handleReadMore(e as any);
          }
        }
      });
    }
  };

  private getDescriptionWithReadMore = (): string => {
    const descriptionHtml = this.getDescription();

    if (!this.needsReadMore) {
      return descriptionHtml;
    }

    // If expanded, show full text with "Read less" link (no ellipsis)
    if (this.isExpanded) {
      const readLessLink = `<span class="${COMPONENT_NAME}__read-more-inline"> <a href="#" class="${COMPONENT_NAME}__read-more-link">${this.getReadLessText()}</a></span>`;
      return `${descriptionHtml}${readLessLink}`;
    }

    // If collapsed, add "Read more" link with ellipsis before and after
    // Ellipsis before link (no space) - will be positioned absolutely at end of second line
    // Ellipsis after "Read more" text
    const readMoreLink = `<span class="${COMPONENT_NAME}__read-more-inline"><span class="${COMPONENT_NAME}__ellipsis-before">...</span><a href="#" class="${COMPONENT_NAME}__read-more-link">${this.getReadMoreText()}</a>...</span>`;
    return `${descriptionHtml}${readMoreLink}`;
  };

  private closePopup = () => {
    this.isVisible = false;
  };

  private handleClose = () => {
    // Set cookie when closing via close button (rejecting cookies)
    setCookie(COOKIE_REJECTED, 'true', { path: '/' });
    this.closePopup();
  };

  private handleCloseKeyDown = (ev: KeyboardEvent) => {
    if (ev.key === 'Enter' || ev.key === ' ') {
      ev.preventDefault();
      this.handleClose();
    }
  };

  private handleSlotClick = (event: MouseEvent) => {
    const target = event.target as HTMLElement;

    // Handle read-more / read-less link clicks via delegation — robust against DOM re-renders
    const readMoreLink = target.closest(`.${COMPONENT_NAME}__read-more-link`);
    if (readMoreLink) {
      event.preventDefault();
      event.stopPropagation();
      if (this.isExpanded) {
        this.handleReadLess(event);
      } else {
        this.handleReadMore(event);
      }
      return;
    }

    const button = target.closest('me-button');

    if (button) {
      // Check if this is the Accept button (has slot="action-primary" attribute)
      const slot = button.getAttribute('slot');
      if (slot === 'action-primary') {
        // Set cookie when accepting cookies
        setCookie(COOKIE_AGREEMENT, 'true', { path: '/' });
      }
      // Close popup without setting rejection cookie
      this.closePopup();
    }
  };

  @Listen('open-cookie', { target: 'window' })
  handleOpenCookie() {
    this.open();
  }

  /**
   * Public method to open the cookie popup programmatically
   */
  @Method()
  async open(): Promise<void> {
    // Check if user has already interacted with the banner
    if (hasUserInteracted()) {
      // User has already accepted or rejected cookies, don't show the banner
      return;
    }

    this.isVisible = true;
    // Check overflow after opening programmatically
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setTimeout(() => this.checkTextOverflow(), OPEN_PROGRAMMATICALLY_DELAY);
      });
    });
  }

  render() {
    const acceptText = this.getAcceptText();
    const collapsedClass = !this.isExpanded && this.needsReadMore ? `${COMPONENT_NAME}__description-text--collapsed` : '';
    const descriptionHtml = this.needsReadMore ? this.getDescriptionWithReadMore() : this.getDescription();

    return (
      <div
        ref={el => (this.slotContainer = el)}
        role="dialog"
        aria-modal="true"
        aria-label="Cookie consent"
        aria-hidden={!this.isVisible}
        class={{ [COMPONENT_NAME]: true, 'is-visible': this.isVisible }}
      >
        <div class={`${COMPONENT_NAME}__content`}>
          <div class={`${COMPONENT_NAME}__description`}>
            <div class={`${COMPONENT_NAME}__description-wrapper`}>
              <me-text
                ref={el => {
                  if (el) {
                    this.descriptionRef = el;
                    // Apply collapsed class immediately to prevent showing full text
                    if (!this.isExpanded && this.needsReadMore) {
                      this.descriptionRef.classList.add(`${COMPONENT_NAME}__description-text--collapsed`);
                    }
                    // Don't call checkTextOverflow from ref callback - it's already called from componentDidLoad
                  }
                }}
                class={`${COMPONENT_NAME}__description-text ${collapsedClass}`}
                innerHTML={descriptionHtml}
              ></me-text>
            </div>
          </div>
          <div class={`${COMPONENT_NAME}__actions`}>
            <me-button slot="action-primary" variant="secondary">
              {acceptText}
            </me-button>
          </div>
        </div>
        <div class={`${COMPONENT_NAME}__close`}>
          <me-button
            iconPosition="only"
            icon="interface--x"
            variant="ghost"
            aria-label={this.closeLabel}
            onClick={this.handleClose}
            onKeyDown={this.handleCloseKeyDown}
          ></me-button>
        </div>
      </div>
    );
  }
}
